import Dexie, { type EntityTable } from "dexie";
import type { PatternProject, ProjectSummary } from "./types";
import { completedCount, occupiedCount } from "./pattern";

interface ProjectRecord {
  id: string;
  name: string;
  width: number;
  height: number;
  paletteJson: string;
  cells: ArrayBuffer;
  completed: ArrayBuffer;
  confidence: ArrayBuffer;
  currentMode: PatternProject["currentMode"];
  currentSelection: number;
  selectedColorId: number | null;
  sourceImageId: string;
  createdAt: string;
  updatedAt: string;
  lastBackupAt: string | null;
  schemaVersion: 1;
}

interface ImageRecord { id: string; source: Blob; preview: Blob; }

const database = new Dexie("pindou-v1") as Dexie & {
  projects: EntityTable<ProjectRecord, "id">;
  images: EntityTable<ImageRecord, "id">;
};

database.version(1).stores({ projects: "id, updatedAt, createdAt", images: "id" });

function arrayBuffer(view: Uint8Array | Uint16Array): ArrayBuffer {
  return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;
}

function toRecord(project: PatternProject): ProjectRecord {
  return {
    id: project.id,
    name: project.name,
    width: project.width,
    height: project.height,
    paletteJson: JSON.stringify(project.palette),
    cells: arrayBuffer(project.cells),
    completed: arrayBuffer(project.completed),
    confidence: arrayBuffer(project.confidence),
    currentMode: project.currentMode,
    currentSelection: project.currentSelection,
    selectedColorId: project.selectedColorId,
    sourceImageId: project.sourceImageId,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    lastBackupAt: project.lastBackupAt,
    schemaVersion: 1
  };
}

function fromRecord(record: ProjectRecord): PatternProject {
  return {
    schemaVersion: 1,
    id: record.id,
    name: record.name,
    width: record.width,
    height: record.height,
    palette: JSON.parse(record.paletteJson),
    cells: new Uint16Array(record.cells.slice(0)),
    completed: new Uint8Array(record.completed.slice(0)),
    confidence: new Uint8Array(record.confidence.slice(0)),
    currentMode: record.currentMode,
    currentSelection: record.currentSelection,
    selectedColorId: record.selectedColorId,
    sourceImageId: record.sourceImageId,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
    lastBackupAt: record.lastBackupAt
  };
}

export const projectRepository = {
  async create(project: PatternProject, source?: Blob, preview?: Blob): Promise<void> {
    await database.transaction("rw", database.projects, database.images, async () => {
      await database.projects.put(toRecord(project));
      if (source && preview) await database.images.put({ id: project.sourceImageId, source, preview });
    });
  },
  async get(id: string): Promise<PatternProject | undefined> {
    const record = await database.projects.get(id);
    return record ? fromRecord(record) : undefined;
  },
  async list(): Promise<ProjectSummary[]> {
    const records = await database.projects.orderBy("updatedAt").reverse().toArray();
    return Promise.all(records.map(async (record) => {
      const project = fromRecord(record);
      const image = await database.images.get(record.sourceImageId);
      return {
        id: project.id,
        name: project.name,
        width: project.width,
        height: project.height,
        colorCount: project.palette.length,
        total: occupiedCount(project),
        completed: completedCount(project),
        updatedAt: project.updatedAt,
        previewBlob: image?.preview
      };
    }));
  },
  async update(project: PatternProject): Promise<void> { await database.projects.put(toRecord(project)); },
  async delete(id: string): Promise<void> {
    const project = await database.projects.get(id);
    await database.transaction("rw", database.projects, database.images, async () => {
      await database.projects.delete(id);
      if (project) await database.images.delete(project.sourceImageId);
    });
  },
  async getImages(sourceImageId: string): Promise<ImageRecord | undefined> { return database.images.get(sourceImageId); },
  async clear(): Promise<void> {
    await database.transaction("rw", database.projects, database.images, async () => {
      await database.projects.clear();
      await database.images.clear();
    });
  }
};
